const EARNINGS_STATUS = Object.freeze({
  ON_HOLD: 'on-hold',
  PAID_OUT: 'paid-out',
});

const EARNINGS_STATUS_INTL = Object.freeze({
  [EARNINGS_STATUS.ON_HOLD]: 'DashboardPage.status.onHold',
  [EARNINGS_STATUS.PAID_OUT]: 'DashboardPage.status.paidOut',
});

const hasRecordedHostTransfer = transaction => {
  const providerTransferId = transaction?.attributes?.metadata?.providerTransferId;
  return typeof providerTransferId === 'string'
    ? providerTransferId.trim().length > 0
    : Boolean(providerTransferId);
};

const hasCompletedHostPayout = transaction => {
  const metadata = transaction?.attributes?.metadata || {};
  const payout = metadata.providerPayout;
  return Boolean(
    hasRecordedHostTransfer(transaction) &&
      payout?.source === 'omise-transfer' &&
      payout.transferId === metadata.providerTransferId &&
      payout.paid === true &&
      typeof payout.paidAt === 'string' &&
      Number.isFinite(Date.parse(payout.paidAt))
  );
};

const MANUAL_PAYOUT_HOLD_MS = 72 * 60 * 60 * 1000;
const COMPLETED_BOOKING_TRANSITIONS = new Set([
  'transition/complete',
  'transition/operator-complete',
  'transition/auto-complete-after-reschedule-booking',
  'transition/operator-complete-after-reschedule-booking',
  'transition/payout-complete',
  'transition/operator-payout-complete',
]);

// Keep cancelled, refunded and disputed bookings outside the automatic manual
// payout rule, matching the existing earnings-summary exclusions.
const EXCLUDED_MANUAL_PAYOUT_TRANSITIONS = new Set([
  'transition/cancel',
  'transition/cancel-by-customer',
  'transition/cancel-by-provider',
  'transition/cancel-from-disputed',
  'transition/auto-cancel-from-disputed',
  'transition/cancel-booking-request',
  'transition/cancel-booking-request-payment',
  'transition/cancel-pay-later-booking-request',
  'transition/complete-after-cancel',
  'transition/operator-complete-after-cancel',
  'transition/initiate-refund',
  'transition/dispute',
  'transition/operator-dispute',
]);

// SceneAccess's manual-payout display policy is booking end + 72 hours.
// This is a display rule, not evidence of a bank transfer. Recorded Omise
// transfers continue to require verified payment, even after the hold expires.
const hasReachedManualPayoutDate = (
  transaction,
  { booking = transaction?.booking, now = Date.now() } = {}
) => {
  if (hasRecordedHostTransfer(transaction)) return false;
  const attributes = transaction?.attributes || {};
  const transitions = (attributes.transitions || []).map(item => item?.transition);
  if (attributes.lastTransition) transitions.push(attributes.lastTransition);
  if (
    booking?.attributes?.state === 'cancelled' ||
    transitions.some(transition => EXCLUDED_MANUAL_PAYOUT_TRANSITIONS.has(transition)) ||
    !transitions.some(transition => COMPLETED_BOOKING_TRANSITIONS.has(transition))
  ) {
    return false;
  }

  const end = booking?.attributes?.end;
  const endMs =
    end instanceof Date ? end.getTime() : typeof end === 'string' ? Date.parse(end) : NaN;
  const nowMs = now instanceof Date ? now.getTime() : now;
  return Number.isFinite(endMs) && Number.isFinite(nowMs) && nowMs >= endMs + MANUAL_PAYOUT_HOLD_MS;
};

const resolveEarningsStatus = (transaction, options) =>
  hasCompletedHostPayout(transaction) || hasReachedManualPayoutDate(transaction, options)
    ? EARNINGS_STATUS.PAID_OUT
    : EARNINGS_STATUS.ON_HOLD;

// Accept only the resolved success key, never raw provider status strings.
const normalizeEarningsStatus = status =>
  status === EARNINGS_STATUS.PAID_OUT ? EARNINGS_STATUS.PAID_OUT : EARNINGS_STATUS.ON_HOLD;

const getEarningsStatusIntlKey = status => EARNINGS_STATUS_INTL[normalizeEarningsStatus(status)];

const earningsStatusLabel = status =>
  ({
    [EARNINGS_STATUS.PAID_OUT]: 'Paid-out',
    [EARNINGS_STATUS.ON_HOLD]: 'On-hold',
  }[normalizeEarningsStatus(status)]);

module.exports = {
  EARNINGS_STATUS,
  EARNINGS_STATUS_INTL,
  earningsStatusLabel,
  getEarningsStatusIntlKey,
  hasCompletedHostPayout,
  hasReachedManualPayoutDate,
  hasRecordedHostTransfer,
  normalizeEarningsStatus,
  resolveEarningsStatus,
  // Compatibility aliases for server helpers that previously used payout naming.
  PAYOUT_STATUS: EARNINGS_STATUS,
  payoutStatusLabel: earningsStatusLabel,
  resolvePayoutStatus: resolveEarningsStatus,
};
